/**
 * Map render snapshot (M6.md). The self-contained, published state a zero-auth
 * viewer/embed renders: the MapSpec + PRE-RESOLVED GeoJSON (coordinates baked
 * in at ingest, so the viewer path does ZERO live geocoding — the margin
 * guardrail, AC-7) + the pinned view + non-removable attribution. It carries
 * ONLY renderable data — never an owner token, source-file URL, geocode-cache
 * internals, or NL history (AC-6). The `etag` keys hard CDN caching and flips
 * only when the real map state changes.
 *
 * Isomorphic hash (js-sha256) so client and server compute the same etag.
 */

import { sha256 } from "js-sha256";
import type { SavedView } from "@/lib/contracts/nl";
import type { MapSpec } from "@/lib/map/mapspec";
import { toGeoJSON, type PointFeatureCollection, type PointSet } from "@/lib/map/pointset";

export const SNAPSHOT_SCHEMA_VERSION = 1;

export interface MapSnapshot {
  schema_version: number;
  title: string;
  /** Published style (the same MapSpec the editor renders). */
  spec: MapSpec;
  /** Pre-resolved features — the viewer never geocodes. */
  geojson: PointFeatureCollection;
  /** Creator's pinned initial view (center/zoom); null → fit to data. */
  pinned_view: { center: [number, number]; zoom: number } | null;
  /** Promoted saved views (AC-10) — the viewer renders each with ZERO LLM calls. */
  saved_views?: SavedView[];
  /** Non-removable attribution string (from the white-label theme). */
  attribution: string;
  point_count: number;
}

export interface SnapshotMeta {
  title: string;
  attribution: string;
  pinnedView?: { center: [number, number]; zoom: number } | null;
  /** Promoted saved views to publish alongside the map (AC-10). */
  savedViews?: SavedView[];
}

/** Build the published snapshot from the current map state. */
export function buildSnapshot(pointSet: PointSet, spec: MapSpec, meta: SnapshotMeta): MapSnapshot {
  return {
    schema_version: SNAPSHOT_SCHEMA_VERSION,
    title: meta.title,
    spec,
    geojson: toGeoJSON(pointSet),
    pinned_view: meta.pinnedView ?? null,
    ...(meta.savedViews && meta.savedViews.length > 0 ? { saved_views: meta.savedViews } : {}),
    attribution: meta.attribution,
    point_count: pointSet.count,
  };
}

/** Stable content hash of a snapshot — the CDN cache key / republish trigger. */
export function snapshotEtag(snapshot: MapSnapshot): string {
  // Stable stringify: the snapshot is already built from ordered structures.
  return sha256(JSON.stringify(snapshot)).slice(0, 32);
}

/**
 * Assert a snapshot is safe to serve to an anonymous viewer: no reserved
 * owner/source keys leaked into the payload. A defensive guard so a future
 * change that stuffs internals into the snapshot fails a test, not in prod.
 */
export function assertViewerSafe(snapshot: MapSnapshot): void {
  const serialized = JSON.stringify(snapshot);
  for (const banned of ["owner_token", "source_url", "sourceUrl", "geocode_cache", "nl_history", "raw_file"]) {
    if (serialized.includes(banned)) throw new Error(`snapshot leaked reserved key: ${banned}`);
  }
}
